import { ValantisBurnAbi } from '../abis/valantis'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/** Upper bound on burns enumerated per user — far above any realistic
 *  number of concurrent pending burn requests. */
const MAX_BURNS = 256n

/**
 * Valantis stHYPE / wstHYPE — burn-request enumeration on the Overseer.
 *
 * Single call: `getBurns(user, 0, MAX_BURNS)` returns parallel `(Burn[],
 * ids[], redeemable[])`. `completed` burns have already been redeemed
 * and are skipped. The Overseer exposes no per-burn ready timestamp, so
 * `status` comes from the `redeemable` flag.
 */
export const readerValantis: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const overseer = entry.withdrawalContract
    if (!overseer) return []

    let res: unknown[]
    try {
      res = (await multicallRetry({
        chain: chainId,
        calls: [
          {
            address: overseer,
            name: 'getBurns',
            params: [user, 0n, MAX_BURNS],
          },
        ] as unknown as never[],
        abi: [ValantisBurnAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    // viem decodes the three return arrays as a tuple `[burns, ids, redeemable]`.
    const cell = res[0] as [unknown[], unknown[], unknown[]] | undefined
    if (!Array.isArray(cell) || !Array.isArray(cell[0])) return []
    const burns = cell[0]
    const ids = cell[1] ?? []
    const redeemable = cell[2] ?? []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < burns.length; i++) {
      const b = burns[i] as
        | { amount?: unknown; completed?: unknown }
        | unknown[]
        | undefined
      if (!b) continue
      const amount = Array.isArray(b) ? toBigInt(b[0]) : toBigInt(b.amount)
      const completed = Array.isArray(b) ? Boolean(b[2]) : Boolean(b.completed)
      if (amount === undefined || completed) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(toBigInt(ids[i]) ?? i),
        amountUnderlying: amount.toString(),
        status: redeemable[i] ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
